'use client';

import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import isEqual from 'lodash/isEqual';
import isString from 'lodash/isString';

import { getContainer } from '@framework/di/getContainer';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { useMessageManager } from '@framework/message';
import { useBreadcrumbUpdates } from '@/creditflowresearch/ui/common/hooks';
import { fetchStatById } from './helpers';
import UserPlan from '@creditflowresearch/user-plan/UserPlan';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { ISingleStatWithData } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Stat';
import { clearDownloadParams, UrlParamsToClear } from '@/creditflowresearch/application/ArticlesAndStats/helpers';
import useDownloadAttachment from '@/creditflowresearch/application/ArticlesAndStats/useDownloadAttachment';
import getServerSideStatBreadcrumbs from '@/creditflowresearch/application/Breadcrumbs/serverSide/getServerSideStatBreadcrumbs';
import { checkAccess } from '@/creditflowresearch/application/User/access/checkAccess';
import { UserPlanFeature } from '@creditflowresearch/user-plan/vo/IUserPlanResponse';

const useStat = (statFromServer: ISingleStatWithData) => {
  const [stat, setStat] = useState<ISingleStatWithData>(statFromServer);
  const [isInitialized, setIsInitialized] = useState<boolean>(!statFromServer.hasServerError);
  const { plan, checkAccessByFeature } = useUserPlanContext();
  const { downloadAttachment } = useDownloadAttachment();
  const router = useRouter();
  const { getErrorMessage } = useMessageManager();
  const { query } = router;

  const { statId, uploadId, displayName } = query;
  const container = getContainer();
  const { updateBreadcrumbs } = useBreadcrumbUpdates();

  useEffect(() => {
    if (statFromServer.hasServerError) {
      getStatById();
      return;
    }
    if (!isEqual(stat, statFromServer)) {
      setStat(statFromServer);
    }
  }, [statFromServer]);

  useEffect(() => {
    const hasAccess = checkAccess([
      UserPlan.isSatisfiedPremiumPlan(plan),
      checkAccessByFeature(UserPlanFeature.NewIssueStats),
    ]);

    if (!hasAccess) {
      return;
    }

    if (!!uploadId && isString(uploadId) && !!displayName && isString(displayName)) {
      downloadAttachment({
        displayName,
        uploadId,
      });
      clearDownloadParams(router, UrlParamsToClear.statId);
    }
  }, []);

  const getStatById = async () => {
    try {
      const stat = await fetchStatById(Number(statId), container);
      setStat(stat);
      updateBreadcrumbs(getServerSideStatBreadcrumbs(router, stat.name));
    } catch (e) {
      AlertManager.error(getErrorMessage(e));
      setStat(null);
    } finally {
      setIsInitialized(true);
    }
  };

  return {
    stat,
    downloadAttachment,
    isInitialized,
  };
};

type StatContextType = ReturnType<typeof useStat>;

export const StatContext = createContext<StatContextType>(null);

export const useStatContext = () => {
  const context = useContext(StatContext);
  if (!context) {
    throw new Error('useStatContext must be used within the StatContext');
  }
  return context;
};

interface IProps {
  children: ReactNode,
  stat: ISingleStatWithData,
}

const StatContextProvider = ({ children, stat }: IProps) => (
  <StatContext.Provider value={ useStat(stat) }>
    { children }
  </StatContext.Provider>
);

export default StatContextProvider;
